import { Link } from 'react-router-dom';
import { Shield, BookOpen, Heart, ArrowRight, Users, Target, Eye } from 'lucide-react';

export default function MissionPage() {
  return (
    <div>
      {/* Hero Section */}
      <section 
        data-testid="mission-hero"
        className="relative pt-28 pb-16 bg-slate-50"
      >
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <span className="inline-block text-blue-600 font-medium text-sm tracking-wide uppercase mb-3">
              Our Mission
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-5">
              Building Brighter Futures Through Education
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              The Hagen-Family-Foundation is dedicated to helping young people attain higher education 
              for a brighter future, while protecting families and communities from scams and fraud.
            </p>
          </div>
        </div>
      </section>

      {/* Main Mission Statement */}
      <section data-testid="mission-statement" className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Our Purpose
            </h2>
            <div className="space-y-5 text-slate-600 leading-relaxed">
              <p className="text-lg">
                The Hagen-Family-Foundation focuses on <strong>protecting seniors and families from phone and online scams</strong>. 
                Through our initiatives like NoMoreScamCalls, we're building technology and education projects to reduce 
                fraud and abuse that targets vulnerable populations.
              </p>
              <p className="text-lg">
                We are committed to <strong>building technology and education projects</strong> that make a real difference. 
                Our approach combines practical tools with community awareness programs, ensuring that families have the 
                resources they need to stay safe in an increasingly digital world.
              </p>
              <p className="text-lg">
                <strong>Transparency and measurable outcomes</strong> guide everything we do. We believe in accountability 
                and work to demonstrate the real-world impact of our programs through clear reporting and community feedback.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Three Pillars: Protect, Educate, Support */}
      <section data-testid="mission-pillars" className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Our Three Pillars
            </h2>
            <p className="text-slate-600">
              Every initiative we undertake is built on these core principles.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Protect */}
            <div 
              data-testid="pillar-protect"
              className="bg-white border border-slate-200 rounded-lg p-8 text-center hover:shadow-lg transition-shadow"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <Shield className="w-8 h-8 text-blue-700" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Protect
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Safeguarding families and seniors from phone scams, online fraud, and deceptive practices 
                through technology and awareness programs.
              </p>
            </div>

            {/* Educate */}
            <div 
              data-testid="pillar-educate"
              className="bg-white border border-slate-200 rounded-lg p-8 text-center hover:shadow-lg transition-shadow"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <BookOpen className="w-8 h-8 text-blue-700" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Educate
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Providing educational support and scholarships to young people, opening doors to 
                higher education and career opportunities.
              </p>
            </div>

            {/* Support */}
            <div 
              data-testid="pillar-support"
              className="bg-white border border-slate-200 rounded-lg p-8 text-center hover:shadow-lg transition-shadow"
            >
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <Heart className="w-8 h-8 text-blue-700" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">
                Support
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">
                Building communities that lift each other up through mentorship, resources, 
                and pathways to meaningful careers.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section data-testid="mission-values" className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-10 text-center">
              What We Stand For
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              <div 
                data-testid="value-transparency"
                className="flex gap-4 p-5 bg-slate-50 rounded-lg"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Eye className="w-6 h-6 text-blue-700" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Transparency</h3>
                  <p className="text-slate-600 text-sm">
                    Open communication and clear reporting on how resources are used and impact achieved.
                  </p>
                </div>
              </div>

              <div 
                data-testid="value-community"
                className="flex gap-4 p-5 bg-slate-50 rounded-lg"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users className="w-6 h-6 text-blue-700" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Community Focus</h3>
                  <p className="text-slate-600 text-sm">
                    Every initiative is designed to strengthen and protect the communities we serve.
                  </p>
                </div>
              </div>

              <div 
                data-testid="value-integrity"
                className="flex gap-4 p-5 bg-slate-50 rounded-lg"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6 text-blue-700" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Measurable Impact</h3>
                  <p className="text-slate-600 text-sm">
                    Commitment to demonstrating real results through clear metrics and outcomes.
                  </p>
                </div>
              </div>

              <div 
                data-testid="value-family"
                className="flex gap-4 p-5 bg-slate-50 rounded-lg"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Heart className="w-6 h-6 text-blue-700" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Family Values</h3>
                  <p className="text-slate-600 text-sm">
                    At our core, we're a family foundation driven by genuine care for others.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 501(c)(3) Status */}
      <section data-testid="mission-status" className="py-16 bg-slate-800">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-5">
              Growing Into Our Mission
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed mb-6">
              The Hagen-Family-Foundation is working toward 501(c)(3) nonprofit status. 
              As we formalize our structure, we remain committed to our core mission of 
              education and community protection.
            </p>
            <p className="text-slate-400">
              Interested in being part of our journey from the beginning?
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="mission-cta" className="py-16 bg-blue-700">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Join Our Mission
          </h2>
          <p className="text-lg text-blue-100 max-w-2xl mx-auto mb-8">
            Whether through partnership, support, or spreading our message, 
            there are many ways to help us create educational opportunities 
            and protect families from fraud.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              data-testid="mission-cta-contact"
              className="inline-flex items-center justify-center gap-2 bg-white text-blue-700 hover:bg-slate-100 rounded-md px-6 py-3 font-medium transition-colors"
            >
              Contact Us
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/initiatives"
              data-testid="mission-cta-initiatives"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white hover:bg-white/10 rounded-md px-6 py-3 font-medium transition-colors"
            >
              View Our Initiatives
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
